// src/pages/Admin/Adherents/DetailsAdherent.jsx
import React, { useEffect, useState } from "react";
import { useUser } from "./../../Home/UserContext";
import { getRoleFromRoleId } from "./../../../utils/roleHelper";
import DashboardNavigation from "./../../DashboardNavigation/DashboardNavigation";
import useScrollToTop from "./../../../hooks/useScollToTop";
import { ToastContainer, toast } from "react-toastify";
import {
  getAllAdherentsWithGroupAndUsers,
  getAdherentById,
} from "../../../services/adherentServices";
import { getSettings } from "../../../services/settingsServices";
import { getGroupsList } from "../../../services/groupServices";
import { getDojos } from "../../../services/dojoServices";
import AdherentDetails from "./AdherentDetails";
import AdherentGroupCategory from "./AdherentGroupCategory";
import AdherentManagers from "./AdherentManagers";
import AdherentPassageCeinture from "./AdherentPassageCeinture";
import AdherentPalmares from "./AdherentPalmares";
import AdherentCompetitions from "./AdherentCompetitions";
import DossierInscription from "./DossierInscription";
import ReglementInscription from "./ReglementInscription";
import AchatsAdherent from "./AchatsAdherent";
import AttendanceList from "./AttendanceList";
import "react-toastify/dist/ReactToastify.css";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";
import {
  FaIdCard,
  FaUsers,
  FaUserCog,
  FaMedal,
  FaTrophy,
  FaFileAlt,
  FaMoneyCheckAlt,
  FaCalendarCheck,
  FaClipboardList,
} from "react-icons/fa";

// 👉 importe la modale d’édition
import EditAdherentAdminModal from "./../ListingAdherentsAdmin/EditAdherentAdminModal";

export default function DetailsAdherent() {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const [adherents, setAdherents] = useState([]);
  const [loadingList, setLoadingList] = useState(true);

  const [selectedAdherentId, setSelectedAdherentId] = useState(null);
  const [selectedAdherent, setSelectedAdherent] = useState(null);
  const [loadingAdherent, setLoadingAdherent] = useState(false);

  const [seasons, setSeasons] = useState([]);
  const [seasonFilter, setSeasonFilter] = useState("");

  const [dojos, setDojos] = useState([]);
  const [selectedDojo, setSelectedDojo] = useState("");
  const [groups, setGroups] = useState([]);
  const [groupFilter, setGroupFilter] = useState("");
  const [showGroupFilter, setShowGroupFilter] = useState(false);

  // État pour la modale d’édition
  const [isEditOpen, setIsEditOpen] = useState(false);

  const toAbsolute = (u) => {
    if (!u) return "";
    const s = String(u).trim();
    if (
      /^https?:\/\//i.test(s) ||
      s.startsWith("data:") ||
      s.startsWith("blob:")
    )
      return s;
    if (!s.startsWith("/uploads")) return s;
    const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
    return `${base}${s}`;
  };

  useEffect(() => {
    (async () => {
      try {
        setLoadingList(true);
        const [adherentsData, settingsData, groupsData, dojoList] =
          await Promise.all([
            getAllAdherentsWithGroupAndUsers(),
            getSettings(),
            getGroupsList(),
            getDojos(),
          ]);

        setAdherents(Array.isArray(adherentsData) ? adherentsData : []);

        const allGroups = Array.isArray(groupsData) ? groupsData : [];
        setDojos(Array.isArray(dojoList) ? dojoList : []);

        if (Array.isArray(dojoList) && dojoList.length === 1) {
          const onlyId = String(dojoList[0].id);
          setSelectedDojo(onlyId);
          setShowGroupFilter(true);
          setGroups(allGroups.filter((g) => String(g.dojo_id) === onlyId));
        } else {
          setGroups(allGroups);
        }

        const active = settingsData?.[0]?.saison_active || "";
        setSeasons(neighborsFromSeason(active));
        setSeasonFilter(active || "");
      } catch (error) {
        console.error("Erreur lors de la récupération des adhérents:", error);
        toast.error("Erreur lors de la récupération des adhérents");
      } finally {
        setLoadingList(false);
      }
    })();
  }, []);

  const handleDojoChange = async (e) => {
    const dojoId = e.target.value;
    setSelectedDojo(dojoId);
    try {
      const allGroups = await getGroupsList();
      const dojoGroups =
        dojoId === ""
          ? allGroups
          : allGroups.filter((g) => String(g.dojo_id) === String(dojoId));
      setGroups(dojoGroups);
      setGroupFilter("");
      setShowGroupFilter(!!dojoId);
    } catch (err) {
      console.error(err);
    }
  };

  const filteredAdherents = React.useMemo(() => {
    const dojoGroupNames = selectedDojo
      ? new Set((groups || []).map((g) => g.nom).filter(Boolean))
      : null;

    return (adherents || [])
      .filter((a) => {
        const seasonsOfA = [
          a.saison_active,
          a.saison,
          a.saison_inscription,
        ].filter(Boolean);
        const matchSeason = !seasonFilter || seasonsOfA.includes(seasonFilter);
        const matchDojo =
          !selectedDojo ||
          !dojoGroupNames ||
          (a?.Groups || []).some((g) => dojoGroupNames.has(g?.nom));
        const matchGroup =
          !groupFilter || (a?.Groups || []).some((g) => g?.nom === groupFilter);
        return matchSeason && matchDojo && matchGroup;
      })
      .sort((a, b) => (a.nom || "").localeCompare(b.nom || "", "fr"));
  }, [adherents, seasonFilter, selectedDojo, groupFilter, groups]);

  const handleSelectAdherent = async (e) => {
    const adherentId = e.target.value;
    if (!adherentId) {
      setSelectedAdherentId(null);
      setSelectedAdherent(null);
      return;
    }
    try {
      setLoadingAdherent(true);
      const adherent = await getAdherentById(adherentId);
      setSelectedAdherentId(adherentId);
      setSelectedAdherent(adherent || null);
    } catch (error) {
      console.error("Erreur lors de la récupération de l'adhérent:", error);
      toast.error("Erreur lors de la récupération de l'adhérent");
    } finally {
      setLoadingAdherent(false);
    }
  };

  // 👉 ouvre la modale d’édition
  const openEditModal = (adherent) => {
    if (adherent) setSelectedAdherent(adherent);
    setIsEditOpen(true);
  };

  const closeEditModal = () => setIsEditOpen(false);

  // 👉 rafraîchit la fiche après sauvegarde depuis la modale
  const refreshSelectedAdherent = async () => {
    if (!selectedAdherentId) return;
    try {
      const updated = await getAdherentById(selectedAdherentId);
      setSelectedAdherent(updated || null);
    } catch (e) {
      console.error(e);
    }
  };

  const NAV_OFFSET = 150;
  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - NAV_OFFSET;
    window.scrollTo({ top: y, behavior: "smooth" });
  };

  const quickLinks = [
    { id: "sec-profil", label: "Profil", Icon: FaIdCard },
    { id: "sec-groupes", label: "Groupes & cat.", Icon: FaUsers },
    { id: "sec-managers", label: "Gestionnaires", Icon: FaUserCog },
    { id: "sec-ceintures", label: "Ceintures", Icon: FaMedal },
    { id: "sec-competitions", label: "Compétitions", Icon: FaTrophy },
    { id: "sec-palmares", label: "Palmarès", Icon: FaMedal },
    { id: "sec-dossier", label: "Dossier inscr.", Icon: FaFileAlt },
    { id: "sec-reglement", label: "Règlement", Icon: FaClipboardList },
    { id: "sec-achats", label: "Achats", Icon: FaMoneyCheckAlt },
    { id: "sec-presences", label: "Présences", Icon: FaCalendarCheck },
  ];

  return (
    <section className="w-full">
      <div className="pt-[150px]" aria-hidden />
      <div className="mx-auto w/full max-w-7xl px-3 sm:px-6">
        <HeaderTitle
          role="Administrateur"
          title="Détails adhérent"
          align="center"
          className="mb-1"
        />
        <div className="mt-3 mb-6">
          <DashboardNavigation role={userRole} />
        </div>

        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur mb-4">
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500">
            <div className="px-5 py-6 sm:px-8 text-white">
              <h2 className="text-lg sm:text-xl font-semibold text-center">
                Fiche complète d’un adhérent (profil, groupes, documents,
                paiements, activités)
              </h2>
              <p className="text-sm/6 opacity-95 max-w-3xl mx-auto text-center mt-1">
                Sélectionnez un adhérent dans la liste ci-dessous pour afficher
                toutes ses informations.
              </p>
            </div>
          </div>

          <div className="px-5 py-5 sm:px-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
              <div>
                <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                  Saison
                </label>
                <select
                  value={seasonFilter}
                  onChange={(e) => setSeasonFilter(e.target.value)}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                >
                  <option value="">Toutes saisons</option>
                  {seasons.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>

              {dojos.length > 1 && (
                <div>
                  <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                    Dojo
                  </label>
                  <select
                    value={selectedDojo}
                    onChange={handleDojoChange}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  >
                    <option value="">Tous les dojos</option>
                    {dojos.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.dojo_nom}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {(selectedDojo || showGroupFilter) && (
                <div>
                  <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                    Groupe
                  </label>
                  <select
                    value={groupFilter}
                    onChange={(e) => setGroupFilter(e.target.value)}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  >
                    <option value="">Tous groupes</option>
                    {groups.map((g) => (
                      <option key={g.id} value={g.nom}>
                        {g.nom}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label
                  htmlFor="adherent-select"
                  className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1"
                >
                  Sélectionnez un adhérent
                </label>
                <select
                  id="adherent-select"
                  value={selectedAdherentId ?? ""}
                  onChange={handleSelectAdherent}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                >
                  <option value="">— Choisissez un adhérent —</option>
                  {filteredAdherents.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.nom} {a.prenom}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-3 text-sm font-medium text-slate-700 dark:text-slate-200">
              Adhérents filtrés : {filteredAdherents.length} /{" "}
              {adherents.length}
            </div>
          </div>

          <div className="px-5 pb-6 sm:px-8">
            {loadingAdherent && (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div
                    key={i}
                    className="animate-pulse rounded-xl border border-slate-200 dark:border-slate-800 p-5 bg-white/60 dark:bg-slate-900/40 h-24"
                  />
                ))}
              </div>
            )}

            {!loadingAdherent && !selectedAdherent && (
              <div className="text-center py-14">
                <div className="mx-auto h-16 w-16 rounded-full bg-gradient-to-br from-blue-500 via-violet-500 to-pink-500 opacity-80" />
                <h3 className="mt-6 text-lg font-semibold text-slate-800 dark:text-slate-200">
                  Aucun adhérent sélectionné
                </h3>
                <p className="mt-1 text-slate-600 dark:text-slate-400">
                  Choisissez un adhérent dans la liste pour afficher sa fiche
                  complète.
                </p>
              </div>
            )}

            {!loadingAdherent && selectedAdherent && (
              <div className="grid grid-cols-1 gap-4">
                <div id="sec-profil" className="col-span-1 scroll-mt-[150px]">
                  {/* 👉 Passe la prop onEdit ici */}
                  <AdherentDetails
                    adherent={selectedAdherent}
                    showTeacherNotes={true}
                    onEdit={(a) => openEditModal(a)}
                  />
                </div>

                <div id="sec-groupes" className="col-span-1 scroll-mt-[150px]">
                  <AdherentGroupCategory adherentId={selectedAdherentId} />
                </div>

                <div id="sec-managers" className="col-span-1 scroll-mt-[150px]">
                  <AdherentManagers adherentId={selectedAdherentId} />
                </div>

                <div
                  id="sec-ceintures"
                  className="col-span-1 scroll-mt-[150px]"
                >
                  <AdherentPassageCeinture adherentId={selectedAdherentId} />
                </div>

                <div
                  id="sec-competitions"
                  className="col-span-1 scroll-mt-[150px]"
                >
                  <AdherentCompetitions adherentId={selectedAdherentId} />
                </div>

                <div id="sec-palmares" className="col-span-1 scroll-mt-[150px]">
                  <AdherentPalmares adherentId={selectedAdherentId} />
                </div>

                <div id="sec-dossier" className="col-span-1 scroll-mt-[150px]">
                  <DossierInscription adherentId={selectedAdherentId} />
                </div>

                <div
                  id="sec-reglement"
                  className="col-span-1 scroll-mt-[150px]"
                >
                  <ReglementInscription
                    adherentId={selectedAdherentId}
                    saison={selectedAdherent.saison_active}
                  />
                </div>

                <div id="sec-achats" className="col-span-1 scroll-mt-[150px]">
                  <AchatsAdherent adherentId={selectedAdherentId} />
                </div>

                <div
                  id="sec-presences"
                  className="col-span-1 scroll-mt-[150px]"
                >
                  <AttendanceList adherentId={selectedAdherentId} />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 👉 Modale d’édition, branchée ici */}
        {selectedAdherent && (
          <EditAdherentAdminModal
            isOpen={isEditOpen}
            onClose={closeEditModal}
            refreshList={refreshSelectedAdherent}
            adherent={selectedAdherent}
          />
        )}

        <ToastContainer />
      </div>
    </section>
  );
}

function neighborsFromSeason(season) {
  const m = /^(\d{4})\s*-\s*(\d{4})$/.exec(season || "");
  if (!m) return [season].filter(Boolean);
  const y1 = parseInt(m[1], 10);
  const y2 = parseInt(m[2], 10);
  return [`${y1 - 1}-${y2 - 1}`, `${y1}-${y2}`, `${y1 + 1}-${y2 + 1}`];
}
